import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
  ExternalLink, Disc3, ArrowLeft, ChevronLeft, ChevronRight,
  Mail, ArrowRight, Music, MapPin, Calendar, Users, Globe, X,
  Instagram, Youtube, Facebook, Twitter,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ReleaseCard from "../components/ReleaseCard";
import ReleaseModal from "../components/ReleaseModal";
import api from "../utils/api";
import { usePublicAuth } from "../context/PublicAuthContext";

const SOCIAL_META = {
  website:    { label: "Website",     icon: Globe,      color: "#6366f1" },
  instagram:  { label: "Instagram",   icon: Instagram,  color: "#E1306C" },
  facebook:   { label: "Facebook",    icon: Facebook,   color: "#1877F2" },
  x:          { label: "X / Twitter", icon: Twitter,    color: "#94a3b8" },
  tiktok:     { label: "TikTok",      icon: Music,      color: "#ffffff" },
  beatport:   { label: "Beatport",    icon: Music,      color: "#00c853" },
  spotify:    { label: "Spotify",     icon: Music,      color: "#1DB954" },
  soundcloud: { label: "SoundCloud",  icon: Music,      color: "#f97316" },
  youtube:    { label: "YouTube",     icon: Youtube,    color: "#ef4444" },
};

// ── Demo Contact Modal ──────────────────────────────────────────────────────────
function DemoContactModal({ label, onClose }) {
  const { t }       = useTranslation();
  const { token }   = usePublicAuth();
  const socialLinks = Object.entries(label.social_links || {}).filter(([, v]) => v);

  // Schließen per Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: "linear-gradient(160deg, #0a1a10 0%, #071510 60%, #050d08 100%)",
          border: "1px solid rgba(16,185,129,0.2)",
          boxShadow: "0 32px 80px rgba(0,0,0,0.8), 0 2px 0 rgba(255,255,255,0.04) inset",
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-4 px-6 py-5 border-b" style={{ borderColor: "rgba(16,185,129,0.1)" }}>
          {label.logo_url ? (
            <img src={label.logo_url} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-emerald-900/40 flex items-center justify-center flex-shrink-0">
              <Disc3 size={18} className="text-emerald-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-500/70">{t("labels.demo_label")}</p>
            <p className="text-white font-bold text-base leading-tight truncate">{label.name}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/10 transition-all flex-shrink-0"
          >
            <X size={15} />
          </button>
        </div>

        {/* Kontaktoptionen */}
        <div className="px-6 py-5 space-y-4">
          {/* Email – nur für eingeloggte User */}
          {label.contact_email && (
            <div>
              {token ? (
                <a
                  href={`mailto:${label.contact_email}?subject=Demo Einreichung – ${encodeURIComponent(label.name)}`}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl transition-all hover:bg-white/[0.06] group"
                  style={{ border: "1px solid rgba(16,185,129,0.15)" }}
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: "rgba(16,185,129,0.12)" }}>
                    <Mail size={14} className="text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-zinc-500 font-medium">{t("labels.demo_contact_email")}</p>
                    <p className="text-sm text-white font-medium truncate group-hover:text-emerald-300 transition-colors">
                      {label.contact_email}
                    </p>
                  </div>
                  <ExternalLink size={12} className="text-zinc-700 group-hover:text-zinc-400 transition-colors flex-shrink-0" />
                </a>
              ) : (
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl"
                  style={{ border: "1px solid rgba(255,255,255,0.05)", background: "rgba(255,255,255,0.02)" }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-zinc-800">
                    <Mail size={14} className="text-zinc-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-zinc-600 font-medium">{t("labels.demo_contact_email")}</p>
                    <p className="text-xs text-zinc-500">{t("labels.demo_login_hint")}</p>
                  </div>
                  <Link to="/login" onClick={onClose}
                    className="text-[10px] font-bold px-2.5 py-1 rounded-lg text-emerald-400 hover:text-white transition-colors flex-shrink-0"
                    style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.2)" }}>
                    {t("labels.demo_login_btn")}
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Social Links */}
          {socialLinks.length > 0 && (
            <div className="space-y-2">
              {socialLinks.map(([key, url]) => {
                const meta = SOCIAL_META[key];
                if (!meta) return null;
                const Icon = meta.icon;
                return (
                  <a key={key} href={url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-3 w-full px-4 py-3 rounded-xl transition-all hover:bg-white/[0.05] group"
                    style={{ border: "1px solid rgba(255,255,255,0.05)" }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${meta.color}18` }}>
                      <Icon size={14} style={{ color: meta.color }} />
                    </div>
                    <p className="text-sm text-zinc-300 font-medium group-hover:text-white transition-colors flex-1 truncate">
                      {meta.label}
                    </p>
                    <ExternalLink size={11} className="text-zinc-700 group-hover:text-zinc-400 transition-colors flex-shrink-0" />
                  </a>
                );
              })}
            </div>
          )}

          {/* Kein Kontakt verfügbar */}
          {!label.contact_email && socialLinks.length === 0 && (
            <p className="text-center text-zinc-500 text-sm py-4">{t("labels.demo_no_contact")}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Green 3D Checkmark ─────────────────────────────────────────────────────────
function GreenCheckmark() {
  return (
    <span
      className="relative group cursor-help inline-flex flex-shrink-0 self-center"
      data-testid="label-managed-badge"
    >
      <span
        className="w-7 h-7 rounded-full flex items-center justify-center"
        style={{
          background: "linear-gradient(145deg, #34d399 0%, #10b981 45%, #059669 100%)",
          boxShadow:
            "0 1px 0 rgba(255,255,255,0.35) inset, 0 -1px 0 rgba(0,0,0,0.2) inset, 0 3px 12px rgba(16,185,129,0.55), 0 1px 3px rgba(0,0,0,0.3)",
        }}
      >
        <svg viewBox="0 0 12 12" fill="none" className="w-3.5 h-3.5">
          <path d="M2 6.5L4.5 9L10 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span
        className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2.5 w-56 px-3 py-2 text-xs text-zinc-200 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none text-center z-50 leading-relaxed font-normal whitespace-normal tracking-normal"
        style={{
          background: "rgba(10,10,10,0.97)",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
        }}
      >
        Dieses Label wird aktiv auf HUWND vom Label selbst verwaltet.
      </span>
    </span>
  );
}

// ── EQ bar decoration ─────────────────────────────────────────────────────────
const EQ_HEIGHTS = [45, 72, 55, 88, 50, 78, 48, 65, 42];

export default function LabelPage() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const [label, setLabel]             = useState(null);
  const [releases, setReleases]       = useState([]);
  const [stats, setStats]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [page, setPage]               = useState(1);
  const [total, setTotal]             = useState(0);
  const [sort, setSort]               = useState("newest");
  const [modalRelease, setModalRelease] = useState(null);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const LIMIT = 24;

  useEffect(() => {
    (async () => {
      try {
        const [lRes, sRes] = await Promise.all([
          api.get(`/labels/${slug}`),
          api.get(`/labels/${slug}/stats`),
        ]);
        setLabel(lRes.data);
        setStats(sRes.data);
      } catch {}
      setLoading(false);
    })();
  }, [slug]);

  const fetchReleases = useCallback(async () => {
    try {
      const res = await api.get(`/labels/${slug}/releases?page=${page}&limit=${LIMIT}&sort=${sort}`);
      setReleases(res.data.releases || []);
      setTotal(res.data.total || 0);
    } catch {}
  }, [slug, page, sort]);

  useEffect(() => { if (label) fetchReleases(); }, [label, fetchReleases]);

  if (loading) return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!label) return (
    <div className="min-h-screen bg-[#080808] flex flex-col">
      <Header />
      <div className="flex-1 flex items-center justify-center text-zinc-500">{t("labels.not_found")}</div>
      <Footer />
    </div>
  );

  const socialLinks  = Object.entries(label.social_links || {}).filter(([, v]) => v);
  const hasTop       = label.top_releases_data?.length > 0;
  const hasRoster    = label.roster?.length > 0;
  const isManaged    = (label.manager_ids || []).length > 0;
  const managerCount = (label.manager_ids || []).length || (label.owner_user_id ? 1 : 0);
  const pages        = Math.ceil(total / LIMIT);

  const SITE        = "https://handsupwillneverdie.de";
  const DEFAULT_IMG = "https://static.prod-images.emergentagent.com/jobs/84f2e1dc-413f-4a02-b5b8-0fa677630a0d/images/69a923a2abd082c3a8bdef45ef751fa07efe9e7e0f3524173ea80206314acf04.png";

  const canonicalUrl = `${SITE}/label/${label.slug}`;
  const ogImage  = label.banner_url || label.logo_url || DEFAULT_IMG;
  const pageTitle = `${label.name} – Label | HUWND`;
  const pageDesc  = label.bio
    ? label.bio.slice(0, 155) + (label.bio.length > 155 ? "…" : "")
    : `${label.name} – Hands-Up Label auf HUWND. ${label.release_count || 0} Releases in der Datenbank.`;

  // Structured Data
  const sameAsLinks = Object.values(label.social_links || {}).filter(Boolean);
  const labelJsonLd = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": label.name,
    "url": canonicalUrl,
    "logo": ogImage,
    "image": ogImage,
    "description": pageDesc,
    ...(label.founded_year ? { "foundingDate": String(label.founded_year) } : {}),
    ...(sameAsLinks.length > 0 ? { "sameAs": sameAsLinks } : {}),
    "memberOf": {
      "@type": "MusicGroup",
      "name": "Hands Up",
      "genre": "Hands Up"
    },
  });
  const breadcrumbJsonLd = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",   "item": SITE },
      { "@type": "ListItem", "position": 2, "name": "Labels", "item": `${SITE}/labels` },
      { "@type": "ListItem", "position": 3, "name": label.name, "item": canonicalUrl },
    ],
  });

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Demo Contact Modal */}
      {demoModalOpen && (
        <DemoContactModal label={label} onClose={() => setDemoModalOpen(false)} />
      )}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="HUWND – Hands Up Will Never Die" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{labelJsonLd}</script>
        <script type="application/ld+json">{breadcrumbJsonLd}</script>
      </Helmet>
      <Header />

      {/* ── HERO ──────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">

        {/* Emerald top accent line (immer full-width) */}
        <div className="absolute top-0 left-0 right-0 h-0.5 pointer-events-none z-10"
          style={{ background: "linear-gradient(90deg, #10b981, #059669, transparent)" }} />

        {/* Background subtle glow (only when no banner) */}
        {!label.banner_url && (
          <div className="absolute inset-0 pointer-events-none">
            {label.logo_url ? (
              <img src={label.logo_url} alt="" className="w-full h-full object-cover scale-125 blur-3xl opacity-[0.08]" />
            ) : (
              <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 80% 60% at 30% 30%, rgba(16,185,129,0.08) 0%, transparent 70%)" }} />
            )}
            <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/85 to-[#050505]" />
          </div>
        )}

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back link */}
          <div className="pt-8 pb-4">
            <Link to="/labels"
              className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm transition-colors group">
              <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
              {t("labels.back")}
            </Link>
          </div>

          {/* ── Contained Banner (mit Default-Fallback) ── */}
          {(label.banner_url || true) && (
            <div className="relative w-full rounded-2xl overflow-hidden h-40 sm:h-52 md:h-60 mb-0"
              style={{ boxShadow: "0 4px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)" }}>
              <img
                src={label.banner_url || "https://static.prod-images.emergentagent.com/jobs/84f2e1dc-413f-4a02-b5b8-0fa677630a0d/images/69a923a2abd082c3a8bdef45ef751fa07efe9e7e0f3524173ea80206314acf04.png"}
                alt=""
                className="w-full h-full object-cover"
                style={{ objectPosition: "center center" }}
              />
              {/* Gradient unten für Logo-Overlap */}
              <div className="absolute inset-0"
                style={{ background: "linear-gradient(to bottom, transparent 40%, rgba(5,5,5,0.7) 100%)" }} />
            </div>
          )}

          {/* ── Profile row: Logo + Info ── */}
          <div className="flex flex-col sm:flex-row items-start gap-6 pb-6 mt-[-52px]">

            {/* Logo – overlaps banner edge when banner present */}
            <div className="relative flex-shrink-0 z-10">
              <div
                className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl overflow-hidden"
                style={{
                  boxShadow: "0 0 0 3px #050505, 0 0 0 4px rgba(16,185,129,0.25), 0 20px 60px rgba(0,0,0,0.8)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                {label.logo_url ? (
                  <img src={label.logo_url} alt={label.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"
                    style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.08) 100%)" }}>
                    <Disc3 size={48} className="text-emerald-700/50" />
                  </div>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0 pt-2 sm:pt-16">

              {/* 3D "Label" Type Badge */}
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl"
                  style={{
                    background: "linear-gradient(145deg, rgba(16,185,129,0.22) 0%, rgba(5,150,105,0.10) 100%)",
                    border: "1px solid rgba(16,185,129,0.28)",
                    boxShadow: "0 1px 0 rgba(52,211,153,0.15) inset, 0 -1px 0 rgba(0,0,0,0.35) inset, 0 2px 12px rgba(0,0,0,0.4), 0 4px 16px rgba(16,185,129,0.08)",
                  }}
                >
                  <Disc3 size={12} className="text-emerald-400" />
                  <span className="text-xs font-bold uppercase tracking-widest text-emerald-400">Label</span>
                </div>
                {managerCount === 0 && (
                  <Link to="/label-studio"
                    className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors border-b border-dashed border-zinc-700 pb-0.5">
                    {t("labels.claim_cta")}
                  </Link>
                )}
              </div>

              {/* Name + Checkmark */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-none mb-3 tracking-tight flex items-center gap-3 flex-wrap">
                {label.name}
                {isManaged && <GreenCheckmark />}
              </h1>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400 mb-4">
                {label.founded_year && (
                  <span className="flex items-center gap-1.5">
                    <Calendar size={13} className="text-emerald-600" />
                    {t("labels.founded", { year: label.founded_year })}
                  </span>
                )}
                {label.country && (
                  <span className="flex items-center gap-1.5">
                    <MapPin size={13} className="text-emerald-600" />
                    {label.country}
                  </span>
                )}
                {hasRoster && (
                  <span className="flex items-center gap-1.5">
                    <Users size={13} className="text-emerald-600" />
                    <span className="font-semibold text-white">{label.roster.length}</span> Artists
                  </span>
                )}
              </div>

              {/* Bio */}
              {label.bio && (
                <p className="text-zinc-400 text-sm sm:text-base leading-relaxed max-w-xl mb-5">
                  {label.bio}
                </p>
              )}

              {/* Social Links */}
              {socialLinks.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {socialLinks.map(([key, url]) => {
                    const meta = SOCIAL_META[key];
                    if (!meta || !url) return null;
                    return (
                      <a key={key} href={url} target="_blank" rel="noreferrer"
                        data-testid={`label-social-${key}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all hover:scale-[1.04] hover:brightness-110"
                        style={{
                          background: `${meta.color}18`,
                          color: meta.color,
                          border: `1px solid ${meta.color}30`,
                        }}>
                        <ExternalLink size={11} />
                        {meta.label}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── BODY ──────────────────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-20 space-y-12">

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: t("labels.stat_releases"),   value: stats.total_releases },
              { label: t("labels.stat_this_year"),  value: stats.releases_this_year },
              { label: t("labels.stat_votes"),      value: stats.total_votes?.toLocaleString() },
              { label: t("labels.stat_artists"),    value: stats.roster_count },
            ].map(s => (
              <div key={s.label}
                className="rounded-2xl p-4 text-center"
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  boxShadow: "0 1px 0 rgba(255,255,255,0.04) inset",
                }}>
                <p className="text-2xl font-black text-white">{s.value ?? "–"}</p>
                <p className="text-xs text-zinc-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Top Releases */}
        {hasTop && (
          <section className="space-y-5">
            <h2 className="text-lg font-black text-white flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-emerald-500 inline-block" />
              {t("labels.top_releases")}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {label.top_releases_data.map(r => (
                <ReleaseCard key={r.id} release={r} onClick={() => setModalRelease(r)} />
              ))}
            </div>
          </section>
        )}

        {/* Roster */}
        {hasRoster && (
          <section className="space-y-5">
            <h2 className="text-lg font-black text-white flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-blue-500 inline-block" />
              Artists <span className="text-zinc-600 font-normal text-base">({label.roster.length})</span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {label.roster.map(a => (
                <Link key={a.id} to={`/artist/${a.slug}`}
                  data-testid={`roster-artist-${a.slug}`}
                  className="group relative block rounded-2xl overflow-hidden"
                  style={{
                    background: "linear-gradient(160deg, #111 0%, #0d0d0d 100%)",
                    boxShadow: "0 2px 0 rgba(255,255,255,0.04) inset, 0 -1px 0 rgba(0,0,0,0.4) inset, 0 8px 24px rgba(0,0,0,0.4)",
                    border: "1px solid rgba(255,255,255,0.06)",
                  }}>
                  <div className="aspect-square overflow-hidden">
                    {a.bild_url ? (
                      <img src={a.bild_url} alt={a.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-zinc-900">
                        <span className="text-4xl font-black text-zinc-700 font-heading">{a.name?.[0]?.toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-1.5">
                      <p className="text-white text-sm font-bold truncate group-hover:text-rose-300 transition-colors leading-snug">{a.name}</p>
                      {a.linked_user_ids?.length > 0 && (
                        <span className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{
                            background: "linear-gradient(145deg, #60a5fa 0%, #3b82f6 45%, #1d4ed8 100%)",
                            boxShadow: "0 1px 0 rgba(255,255,255,0.3) inset, 0 2px 6px rgba(59,130,246,0.5)",
                          }}>
                          <svg viewBox="0 0 12 12" fill="none" style={{ width: "7px", height: "7px" }}>
                            <path d="M2 6.5L4.5 9L10 3.5" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                      )}
                    </div>
                    {a.releases_count > 0 && (
                      <p className="text-zinc-600 text-xs mt-0.5">{a.releases_count} Releases</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ── Team ─────────────────────────────────────────────────────────── */}
        {label.managers?.length > 0 && (
          <section>
            <h2 className="text-sm font-bold text-zinc-500 uppercase tracking-widest mb-4">{t("labels.page.team_title")}</h2>
            <div className="flex flex-wrap gap-3">
              {label.managers.map(m => {
                const inner = (
                  <>
                    <div className="w-9 h-9 rounded-full overflow-hidden bg-zinc-800 flex-shrink-0">
                      {m.avatar_url
                        ? <img src={m.avatar_url} alt="" className="w-full h-full object-cover" />
                        : <span className="w-full h-full flex items-center justify-center text-xs font-bold text-zinc-500">{m.username?.[0]?.toUpperCase()}</span>}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white leading-none group-hover:text-emerald-300 transition-colors">{m.username}</p>
                      <p className="text-[11px] text-emerald-500/80 mt-0.5 font-medium">{m.role_title || "Label Manager"}</p>
                    </div>
                  </>
                );
                const cls = "group flex items-center gap-3 px-4 py-3 rounded-2xl bg-zinc-900/50 border border-white/[0.05] transition-colors";
                return m.username ? (
                  <Link key={m.auth0_id} to={`/user/${encodeURIComponent(m.username)}`}
                    className={`${cls} hover:border-emerald-500/20 hover:bg-zinc-900`}>
                    {inner}
                  </Link>
                ) : (
                  <div key={m.auth0_id} className={cls}>{inner}</div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Demo einreichen CTA ───────────────────────────────────────────── */}
        <section
          data-testid="label-demo-cta"
          className="relative rounded-2xl overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #030e07 0%, #071a10 40%, #0a2718 70%, #071a10 100%)",
            border: "1px solid rgba(16,185,129,0.18)",
            boxShadow: "0 2px 0 rgba(255,255,255,0.04) inset, 0 -1px 0 rgba(0,0,0,0.4) inset, 0 16px 48px rgba(0,0,0,0.5)",
          }}
        >
          {/* EQ decoration */}
          <div className="absolute right-6 inset-y-0 flex items-end gap-[3px] py-5 opacity-[0.12] pointer-events-none">
            {EQ_HEIGHTS.map((h, i) => (
              <div key={i} className="w-[3px] rounded-full bg-emerald-400" style={{ height: `${h}%` }} />
            ))}
          </div>

          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-6 px-6 sm:px-8 py-8">
            {/* Icon */}
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: "rgba(16,185,129,0.14)",
                border: "1px solid rgba(16,185,129,0.22)",
                boxShadow: "0 1px 0 rgba(52,211,153,0.1) inset",
              }}
            >
              <Music size={20} className="text-emerald-400" />
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] mb-1" style={{ color: "rgba(52,211,153,0.7)" }}>
                {t("labels.demo_label")}
              </p>
              <h3 className="text-white font-black text-xl sm:text-2xl leading-tight mb-1">
                {t("labels.demo_heading")}
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed max-w-md">
                {t("labels.demo_desc")}
              </p>
            </div>

            {/* CTA Button → öffnet Modal */}
            <button
              onClick={() => setDemoModalOpen(true)}
              data-testid="label-demo-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm text-white transition-all hover:brightness-110 active:scale-[0.98] flex-shrink-0 cursor-pointer"
              style={{
                background: "linear-gradient(110deg, rgba(16,185,129,0.85) 0%, rgba(5,150,105,0.95) 100%)",
                boxShadow: "0 2px 0 rgba(255,255,255,0.08) inset, 0 4px 16px rgba(16,185,129,0.25)",
              }}
            >
              <Mail size={14} />
              {t("labels.demo_btn")}
              <ArrowRight size={13} />
            </button>
          </div>
        </section>

        {/* All Releases */}
        <section className="space-y-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h2 className="text-lg font-black text-white flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-rose-500 inline-block" />
              {t("labels.all_releases")} <span className="text-zinc-500 font-normal text-base">({total})</span>
            </h2>
            <select
              value={sort}
              onChange={e => { setSort(e.target.value); setPage(1); }}
              className="text-xs bg-zinc-800/80 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            >
              <option value="newest">{t("labels.sort_newest")}</option>
              <option value="oldest">{t("labels.sort_oldest")}</option>
              <option value="votes">{t("labels.sort_votes")}</option>
            </select>
          </div>

          {releases.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {releases.map(r => (
                <ReleaseCard key={r.id} release={r} onClick={() => setModalRelease(r)} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 text-zinc-600 text-sm">Noch keine Releases.</div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-4">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-xl disabled:opacity-30 hover:bg-zinc-800 transition-colors text-zinc-400"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm text-zinc-500">{page} / {pages}</span>
              <button
                onClick={() => setPage(p => Math.min(pages, p + 1))}
                disabled={page >= pages}
                className="p-2 rounded-xl disabled:opacity-30 hover:bg-zinc-800 transition-colors text-zinc-400"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </section>
      </div>

      <Footer />
      {modalRelease && <ReleaseModal release={modalRelease} onClose={() => setModalRelease(null)} />}
    </div>
  );
}
